import { ApiError } from '../api/problem'

/**
 * Turns a failed mutation into something worth reading.
 *
 * A `409` is the interesting case: the API uses it for a rule the domain forbids, and
 * its `detail` names the rule ("This campaign already has a city."). Swallowing that
 * into "something went wrong" would throw away the only part the user can act on.
 * `400` is the same story here — the campaign controllers answer `ProblemDetails` with
 * a written reason for the rules that are not conflicts ("You already have a PC in this
 * campaign."), so it is surfaced rather than generalized.
 *
 * `forbidden` is the one message that cannot be shared: who is allowed to write is a
 * per-domain fact, so each caller supplies its own sentence.
 */
export function describeApiFailure(error: unknown, forbidden: string): string {
  if (!(error instanceof ApiError)) return 'Something went wrong.'

  switch (error.kind) {
    case 'conflict':
      return error.detail ?? error.title ?? 'That change is not allowed.'
    case 'forbidden':
      return forbidden
    case 'not-found':
      return 'That is no longer there — reload the page.'
    case 'validation':
      return (
        error
          .fieldMessages()
          .map((entry) => entry.message)
          .join(' ') || error.message
      )
    case 'network':
      return 'Could not reach the server. Your change was not saved.'
    default:
      return error.message
  }
}
